import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

type Language = 'cs' | 'en' | 'ar';
type Direction = 'ltr' | 'rtl';

interface I18nContextType {
  language: Language;
  direction: Direction;
  setLanguage: (lang: Language) => void;
  t: (key: string, fallback?: string) => string;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

const isRtl = (lang: Language): Direction => (lang === 'ar' ? 'rtl' : 'ltr');

const dictionaries: Record<Language, Record<string, string>> = {
  cs: {
    'common.skip_to_content': 'Přeskočit na obsah',
    'common.skip_to_navigation': 'Přeskočit na navigaci',
    'cookies.banner_text': 'Tento web používá cookies pro analýzu a marketing. Můžete si vybrat, které kategorie povolíte.',
    'cookies.accept_all': 'Přijmout vše',
    'cookies.reject': 'Pouze nezbytné',
    'cookies.preferences': 'Nastavení',
    'cookies.preferences_title': 'Nastavení cookies',
    'cookies.preferences_desc': 'Zvolte, které kategorie cookies chcete povolit.',
    'cookies.necessary_title': 'Nezbytné',
    'cookies.necessary_desc': 'Nutné pro správné fungování webu. Nelze vypnout.',
    'cookies.analytics_title': 'Analytické',
    'cookies.analytics_desc': 'Pomáhají nám pochopit, jak web používáte (GA4 / GTM).',
    'cookies.marketing_title': 'Marketingové',
    'cookies.marketing_desc': 'Umožňují zobrazovat relevantní reklamy (Meta Pixel).',
    'cookies.cancel': 'Zrušit',
    'cookies.save': 'Uložit nastavení',
  },
  en: {
    'common.skip_to_content': 'Skip to content',
    'common.skip_to_navigation': 'Skip to navigation',
    'cookies.banner_text': 'This site uses cookies for analytics and marketing. You can choose which categories to allow.',
    'cookies.accept_all': 'Accept all',
    'cookies.reject': 'Necessary only',
    'cookies.preferences': 'Preferences',
    'cookies.preferences_title': 'Cookie preferences',
    'cookies.preferences_desc': 'Choose which cookie categories you want to allow.',
    'cookies.necessary_title': 'Necessary',
    'cookies.necessary_desc': 'Required for the website to function. Cannot be disabled.',
    'cookies.analytics_title': 'Analytics',
    'cookies.analytics_desc': 'Help us understand how you use the site (GA4 / GTM).',
    'cookies.marketing_title': 'Marketing',
    'cookies.marketing_desc': 'Enable relevant advertising (Meta Pixel).',
    'cookies.cancel': 'Cancel',
    'cookies.save': 'Save preferences',
  },
  ar: {
    'common.skip_to_content': 'تخطي إلى المحتوى',
    'common.skip_to_navigation': 'تخطي إلى التنقل',
    'cookies.banner_text': 'يستخدم هذا الموقع ملفات تعريف الارتباط للتحليلات والتسويق. يمكنك اختيار الفئات المسموح بها.',
    'cookies.accept_all': 'قبول الكل',
    'cookies.reject': 'الضرورية فقط',
    'cookies.preferences': 'الإعدادات',
    'cookies.preferences_title': 'إعدادات ملفات تعريف الارتباط',
    'cookies.preferences_desc': 'اختر فئات ملفات تعريف الارتباط التي تريد السماح بها.',
    'cookies.necessary_title': 'ضرورية',
    'cookies.necessary_desc': 'مطلوبة لعمل الموقع. لا يمكن تعطيلها.',
    'cookies.analytics_title': 'تحليلية',
    'cookies.analytics_desc': 'تساعدنا على فهم كيفية استخدامك للموقع.',
    'cookies.marketing_title': 'تسويقية',
    'cookies.marketing_desc': 'تمكين الإعلانات ذات الصلة (Meta Pixel).',
    'cookies.cancel': 'إلغاء',
    'cookies.save': 'حفظ الإعدادات',
  },
};

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLang] = useState<Language>(() => {
    const saved = localStorage.getItem('lang') as Language | null;
    return saved || 'cs';
  });

  const direction = useMemo<Direction>(() => isRtl(language), [language]);

  useEffect(() => {
    // Set HTML attributes
    document.documentElement.lang = language;
    document.documentElement.dir = direction;
    localStorage.setItem('lang', language);
  }, [language, direction]);

  const setLanguage = (lang: Language) => setLang(lang);

  const t = (key: string, fallback?: string) => {
    const dict = dictionaries[language] || {};
    return dict[key] || fallback || key;
  };

  return (
    <I18nContext.Provider value={{ language, direction, setLanguage, t }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = (): I18nContextType => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
};

